import { useState, useEffect } from 'react'
import {
  Settings, Check, Eye, EyeOff, Save,
  ShoppingBag, Map, BarChart2, TrendingUp, ArrowLeft, Info,
  Zap, Loader, CheckCircle, RefreshCw, AlertTriangle, Pencil, Trash2, Globe, Search,
  Key, Copy, Bot
} from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

const NODE_API = ''

// Tarjeta "API e integraciones": clave para que el agente IA del cliente use el MCP de
// esta plataforma (/mcp) en vez del panel. La clave solo se muestra en texto plano UNA vez, al
// generarla — igual que Video Studio. No usa el token de sesión humana (AuthContext) porque está
// pensada para una integración máquina-a-máquina, no para un usuario logueado en el navegador.
function AssistantKeyCard() {
  const { t } = useLanguage()
  const [status, setStatus] = useState(null)
  const [newKey, setNewKey] = useState(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  const loadStatus = async () => {
    try {
      const res = await fetch(`${NODE_API}/api/assistant-key/status`, { signal: AbortSignal.timeout(4000) })
      setStatus(await res.json())
    } catch {
      setStatus(null)
    }
  }

  useEffect(() => { loadStatus() }, [])

  const handleGenerate = async () => {
    setBusy(true)
    try {
      const res = await fetch(`${NODE_API}/api/assistant-key/generate`, { method: 'POST' })
      const data = await res.json()
      if (data.key) setNewKey(data.key)
      await loadStatus()
    } finally {
      setBusy(false)
    }
  }

  const handleRevoke = async () => {
    setBusy(true)
    try {
      await fetch(`${NODE_API}/api/assistant-key/revoke`, { method: 'POST' })
      setNewKey(null)
      await loadStatus()
    } finally {
      setBusy(false)
    }
  }

  const handleCopy = () => {
    if (!newKey) return
    navigator.clipboard?.writeText(newKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="p-5 bg-[#111111] border border-[#2a2a2a] rounded-xl">
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a]">
          <Bot className="w-4 h-4 text-[#8b949e]" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[#dddddd]">{t('settings.aiTitle')}</h3>
          <p className="text-xs text-[#8b949e] mt-0.5">{t('settings.aiDesc')}</p>
        </div>
      </div>

      {newKey && (
        <div className="flex items-start gap-2 p-3 bg-[#0d1f0d] border border-[#1a3a1a] rounded-lg mb-3">
          <Key className="w-3.5 h-3.5 text-[#22c55e] shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-[#22c55e] mb-1">
              {t('settings.aiCopyNow')}
              <code className="mx-1 text-[#9ae6b4]">/setkey GLOBALMARKET_API_KEY {newKey.slice(0, 10)}…</code>
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate px-2 py-1 bg-[#0a0a0a] border border-[#1a3a1a] rounded text-xs text-[#9ae6b4] font-mono">
                {newKey}
              </code>
              <button
                onClick={handleCopy}
                className="shrink-0 flex items-center gap-1 px-2 py-1 rounded text-xs text-[#8b949e] hover:text-[#cccccc] border border-[#2a2a2a]"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? t('settings.aiCopied') : t('settings.aiCopy')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${status?.configured ? 'bg-[#22c55e]' : 'bg-[#ef4444]'}`} />
        <span className="text-xs text-[#8b949e]">
          {status?.configured ? `${t('settings.aiConfigured')} (${status.preview})` : t('settings.aiNotConfigured')}
        </span>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={handleGenerate}
          disabled={busy}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#0ea5e9] hover:bg-[#0284c7] text-white transition-all disabled:opacity-50"
        >
          {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}
          {status?.configured ? t('settings.aiRegenerate') : t('settings.aiGenerate')}
        </button>
        {status?.configured && (
          <button
            onClick={handleRevoke}
            disabled={busy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#1a0d0d] hover:bg-[#250d0d] text-[#ef4444] border border-[#3a1a1a] transition-all disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('settings.aiRevoke')}
          </button>
        )}
      </div>
    </div>
  )
}

function getServices(t) {
  return [
    {
      id: 'google_trends',
      name: 'Google Trends',
      icon: TrendingUp,
      statusKey: 'google_trends',
      description: t('settings.services.google_trends.description'),
      how: t('settings.services.google_trends.how') || [],
      sharedKeyNote: t('settings.services.google_trends.sharedKeyNote'),
      freeNote: t('settings.services.google_trends.freeNote'),
      rateNote: t('settings.services.google_trends.rateNote'),
      renewNote: null,
      fields: [
        { key: 'google_trends_rapidapi_key', label: 'RapidAPI Key', secret: true, placeholder: 'X-RapidAPI-Key (~50 chars)' },
        { key: 'google_trends_host', label: 'API Host', secret: false, placeholder: 'google-trends8.p.rapidapi.com' },
      ],
    },
    {
      id: 'amazon',
      name: 'Amazon Real-Time Data',
      icon: Globe,
      statusKey: 'amazon',
      description: t('settings.services.amazon.description'),
      how: t('settings.services.amazon.how') || [],
      sharedKeyNote: t('settings.services.amazon.sharedKeyNote'),
      freeNote: null,
      rateNote: null,
      renewNote: null,
      fields: [
        { key: 'amazon_rapidapi_key', label: 'RapidAPI Key', secret: true, placeholder: 'X-RapidAPI-Key (~50 chars)' },
        { key: 'amazon_host', label: 'API Host (RapidAPI)', secret: true, placeholder: 'e.g. real-time-amazon-data.p.rapidapi.com' },
      ],
    },
    {
      id: 'pinterest',
      name: 'Pinterest Trends',
      icon: Zap,
      statusKey: 'pinterest',
      description: t('settings.services.pinterest.description'),
      how: t('settings.services.pinterest.how') || [],
      sharedKeyNote: null,
      freeNote: null,
      rateNote: null,
      renewNote: null,
      fields: [
        { key: 'pinterest_rapidapi_key', label: 'RapidAPI Key', secret: true, placeholder: 'X-RapidAPI-Key (~50 chars)' },
        { key: 'pinterest_host', label: 'API Host (RapidAPI)', secret: true, placeholder: 'e.g. pinterest-scraper-v2.p.rapidapi.com' },
      ],
    },
    {
      id: 'google_maps',
      name: 'Google Maps / Places API',
      icon: Map,
      statusKey: 'google_maps',
      description: t('settings.services.google_maps.description'),
      how: t('settings.services.google_maps.how') || [],
      sharedKeyNote: null,
      freeNote: null,
      rateNote: null,
      renewNote: null,
      fields: [
        { key: 'google_maps_key', label: 'API Key', secret: true, placeholder: 'AIzaSy...' },
      ],
    },
    {
      id: 'facebook',
      name: t('settings.services.facebook.name'),
      icon: BarChart2,
      statusKey: 'facebook',
      description: t('settings.services.facebook.description'),
      how: t('settings.services.facebook.how') || [],
      sharedKeyNote: null,
      freeNote: null,
      rateNote: null,
      renewNote: t('settings.services.facebook.renewNote'),
      fields: [
        { key: 'fb_app_id',       label: 'App ID',         secret: true, placeholder: 'Facebook App ID' },
        { key: 'fb_app_secret',   label: 'App Secret',     secret: true, placeholder: 'App Secret' },
        { key: 'fb_access_token', label: 'Access Token',   secret: true, placeholder: 'EAAU8GX5... (token 60 días)' },
      ],
    },
    {
      id: 'facebook_rapidapi',
      name: t('settings.services.facebook_rapidapi.name'),
      icon: BarChart2,
      statusKey: 'facebook_rapidapi',
      description: t('settings.services.facebook_rapidapi.description'),
      how: t('settings.services.facebook_rapidapi.how') || [],
      sharedKeyNote: null,
      freeNote: null,
      rateNote: null,
      renewNote: t('settings.services.facebook_rapidapi.renewNote'),
      fields: [
        { key: 'fb_rapidapi_key',  label: 'RapidAPI Key',        secret: true,  placeholder: 'X-RapidAPI-Key (~50 chars)' },
        { key: 'fb_rapidapi_host', label: 'API Host (RapidAPI)', secret: false, placeholder: 'facebook-ads-library-scraper-api.p.rapidapi.com' },
      ],
    },
    {
      id: 'aliexpress',
      name: 'AliExpress Dropshipping API',
      icon: ShoppingBag,
      statusKey: 'aliexpress',
      description: t('settings.services.aliexpress.description'),
      how: t('settings.services.aliexpress.how') || [],
      sharedKeyNote: null,
      freeNote: null,
      rateNote: null,
      renewNote: null,
      fields: [
        { key: 'ae_app_key',    label: 'App Key',    secret: true, placeholder: 'App Key' },
        { key: 'ae_app_secret', label: 'App Secret', secret: true, placeholder: 'App Secret' },
      ],
    },
    {
      id: 'google_ads',
      name: 'Google Ads — Keyword Planner',
      icon: Search,
      statusKey: 'google_ads',
      description: t('settings.services.google_ads.description'),
      how: t('settings.services.google_ads.how') || [],
      sharedKeyNote: null,
      freeNote: t('settings.services.google_ads.freeNote'),
      rateNote: null,
      renewNote: null,
      fields: [
        { key: 'google_ads_developer_token', label: 'Developer Token',   secret: true,  placeholder: 'Tu developer token de Google Ads API' },
        { key: 'google_ads_client_id',       label: 'OAuth2 Client ID',  secret: false, placeholder: 'xxxx.apps.googleusercontent.com' },
        { key: 'google_ads_client_secret',   label: 'OAuth2 Client Secret', secret: true, placeholder: 'GOCSPX-...' },
        { key: 'google_ads_refresh_token',   label: 'Refresh Token',     secret: true,  placeholder: '1//0g...' },
        { key: 'google_ads_customer_id',     label: 'Customer ID',       secret: false, placeholder: 'Sin guiones: 1234567890' },
      ],
    },
    {
      id: 'dataforseo',
      name: 'DataForSEO — Keyword Research',
      icon: Search,
      statusKey: 'dataforseo',
      description: t('settings.services.dataforseo.description'),
      how: t('settings.services.dataforseo.how') || [],
      sharedKeyNote: null,
      freeNote: t('settings.services.dataforseo.freeNote'),
      rateNote: t('settings.services.dataforseo.rateNote'),
      renewNote: null,
      fields: [
        { key: 'dataforseo_login',    label: 'Login (email)',  secret: false, placeholder: 'Tu email de DataForSEO' },
        { key: 'dataforseo_password', label: 'Password (API)', secret: true,  placeholder: 'Contraseña del dashboard DataForSEO' },
      ],
    },
    {
      id: 'similarweb',
      name: 'SimilarWeb — Tráfico de competidores',
      icon: Globe,
      statusKey: 'similarweb',
      description: t('settings.services.similarweb.description'),
      how: t('settings.services.similarweb.how') || [],
      sharedKeyNote: null,
      freeNote: null,
      rateNote: null,
      renewNote: null,
      fields: [
        { key: 'similarweb_rapidapi_key', label: 'RapidAPI Key', secret: true, placeholder: 'X-RapidAPI-Key (~50 chars)' },
        { key: 'similarweb_host',         label: 'API Host (RapidAPI)', secret: true, placeholder: 'e.g. similarweb-data.p.rapidapi.com' },
      ],
    },
    {
      id: 'semrush',
      name: 'SEMrush — Autoridad de dominio',
      icon: BarChart2,
      statusKey: 'semrush',
      description: t('settings.services.semrush.description'),
      how: t('settings.services.semrush.how') || [],
      sharedKeyNote: null,
      freeNote: null,
      rateNote: null,
      renewNote: null,
      fields: [
        { key: 'semrush_rapidapi_key', label: 'RapidAPI Key', secret: true, placeholder: 'X-RapidAPI-Key (~50 chars)' },
        { key: 'semrush_host',         label: 'API Host (RapidAPI)', secret: true, placeholder: 'e.g. semrush-website-traffic-checker.p.rapidapi.com' },
      ],
    },
    {
      id: 'ahrefs',
      name: 'Ahrefs — Backlinks y SEO',
      icon: Search,
      statusKey: 'ahrefs',
      description: t('settings.services.ahrefs.description'),
      how: t('settings.services.ahrefs.how') || [],
      sharedKeyNote: null,
      freeNote: null,
      rateNote: null,
      renewNote: null,
      fields: [
        { key: 'ahrefs_rapidapi_key', label: 'RapidAPI Key', secret: true, placeholder: 'X-RapidAPI-Key (~50 chars)' },
        { key: 'ahrefs_host',        label: 'API Host (RapidAPI)', secret: true, placeholder: 'e.g. domain-metrics-check.p.rapidapi.com' },
      ],
    },
  ]
}

function ApiHealthNote({ health, serviceId }) {
  const { t } = useLanguage()
  if (!health) return null
  const { status, remaining, limit, days_left } = health

  // Never configured
  if (status === 'not_configured') return null

  // AliExpress: configured but not testable via health check
  if (status === 'configured_not_tested') {
    return (
      <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg">
        <span style={{ color: '#5a5a5a', fontSize: '0.7rem' }}>ℹ</span>
        <span className="text-xs text-[#6b6b6b]">Claves guardadas — se verifican al realizar una búsqueda</span>
      </div>
    )
  }

  // ok with no quota info (lightweight polling result)
  if (status === 'ok' && remaining === undefined && days_left === undefined) {
    if (serviceId === 'google_maps') {
      return (
        <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg">
          <span style={{ color: '#5a5a5a', fontSize: '0.7rem' }}>ℹ</span>
          <span className="text-xs text-[#6b6b6b]">API activa — cuota de $200/mes incluida por Google</span>
        </div>
      )
    }
    return null
  }

  // RapidAPI quota (Amazon, Pinterest)
  if (remaining !== undefined && limit !== undefined) {
    if (remaining === 0) {
      return (
        <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg">
          <span style={{ color: '#d97706', fontSize: '0.7rem' }}>⚠</span>
          <span className="text-xs text-[#6b6b6b]">
            Cuota mensual del plan gratuito agotada — se renueva automáticamente el 1 del próximo mes
          </span>
        </div>
      )
    }
    const pct = Math.round((remaining / limit) * 100)
    const low = remaining <= 5
    const warn = remaining <= Math.round(limit * 0.15)
    const color = low ? '#ef4444' : warn ? '#d97706' : '#5a5a5a'
    return (
      <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg">
        <span style={{ color, fontSize: '0.7rem' }}>{low || warn ? '⚠' : 'ℹ'}</span>
        <span className="text-xs" style={{ color }}>
          {remaining} / {limit} llamadas restantes este mes ({pct}%)
          {low ? ' — considera actualizar el plan' : ''}
        </span>
      </div>
    )
  }

  // Facebook: un token vencido se avisa con los pasos para activarlo (antes que los días restantes)
  if (status === 'expired') {
    return (
      <div className="flex items-start gap-2 mt-3 px-3 py-2 bg-[#0d0d0d] border border-[#3a1a1a] rounded-lg">
        <span style={{ color: '#ef4444', fontSize: '0.7rem' }}>⚠</span>
        <span className="text-xs text-[#ef4444]">
          <strong>{t('settings.fbTokenExpired')}.</strong> {t('settings.fbTokenSteps')}
        </span>
      </div>
    )
  }

  // Facebook token expiry
  if (days_left !== undefined && days_left >= 0) {
    const low = days_left <= 7
    const warn = days_left <= 14
    const color = low ? '#ef4444' : warn ? '#d97706' : '#5a5a5a'
    return (
      <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg">
        <span style={{ color, fontSize: '0.7rem' }}>{low || warn ? '⚠' : 'ℹ'}</span>
        <span className="text-xs" style={{ color }}>
          {t('settings.fbExpiresIn').replace('{n}', days_left)}{low ? t('settings.fbRenewUrgent') : warn ? t('settings.fbRenewSoon') : ''}
        </span>
      </div>
    )
  }

  if (status === 'quota_exceeded') {
    return (
      <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#0d0d0d] border border-[#3a1a1a] rounded-lg">
        <span style={{ color: '#ef4444', fontSize: '0.7rem' }}>⚠</span>
        <span className="text-xs text-[#ef4444]">Cuota mensual agotada — se renueva el próximo mes</span>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[#0d0d0d] border border-[#3a1a1a] rounded-lg">
        <span style={{ color: '#ef4444', fontSize: '0.7rem' }}>⚠</span>
        <span className="text-xs text-[#ef4444]">{health.message || 'Error al verificar — revisa que la clave sea válida'}</span>
      </div>
    )
  }

  return null
}

function ServiceCard({ service, savedValues, status, health, onSave, onClear, serverAvailable }) {
  const { t } = useLanguage()
  const Icon = service.icon
  const [editing, setEditing]   = useState(false)
  const [values, setValues]     = useState({})
  const [visible, setVisible]   = useState({})
  const [saving, setSaving]     = useState(false)
  const [clearing, setClearing] = useState(false)
  const [saveOk, setSaveOk]     = useState(false)
  const [showHow, setShowHow]   = useState(false)

  useEffect(() => {
    const init = {}
    service.fields.forEach(f => { if (f.key) init[f.key] = '' })
    setValues(init)
    setEditing(false)
    setVisible({})
  }, [savedValues, service.fields])

  const isConfigured = status[service.statusKey] === true
  const hasNewValues = service.fields.filter(f => f.key).some(f => (values[f.key] || '').length > 0)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(values)
      setSaveOk(true)
      setTimeout(() => setSaveOk(false), 2500)
    } catch {
      // el motivo ya se muestra en el aviso general de la página (handleSave de SettingsPage)
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    setClearing(true)
    try {
      const fieldsToClear = service.fields.filter(f => f.key).map(f => f.key)
      await onClear(fieldsToClear)
    } finally {
      setClearing(false)
    }
  }

  const handleCancelEdit = () => {
    const reset = {}
    service.fields.forEach(f => { reset[f.key] = '' })
    setValues(reset)
    setVisible({})
    setEditing(false)
  }

  const statusColor = isConfigured ? 'text-[#22c55e]' : 'text-[#ef4444]'
  const statusDot   = isConfigured ? 'bg-[#22c55e]'   : 'bg-[#ef4444]'
  const statusLabel = isConfigured ? t('settings.configured') : t('settings.pending')

  return (
    <div className={`p-5 rounded-xl border transition-all ${
      isConfigured
        ? 'bg-[#111111] border-[#2a2a2a]'
        : 'bg-[#110d0d] border-[#3a1a1a]'
    }`}>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a]">
            <Icon className="w-4 h-4 text-[#8b949e]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#dddddd]">{service.name}</h3>
            <p className="text-xs text-[#8b949e] mt-0.5 max-w-xs">{service.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${statusDot}`} />
          <span className={`text-xs font-medium ${statusColor}`}>{statusLabel}</span>
        </div>
      </div>

      {/* Free note */}
      {service.freeNote && (
        <div className="flex items-start gap-2 p-3 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg mb-3">
          <CheckCircle className="w-3.5 h-3.5 text-[#5a5a5a] shrink-0 mt-0.5" />
          <p className="text-xs text-[#6b6b6b]">{service.freeNote}</p>
        </div>
      )}

      {/* Rate limit note */}
      {service.rateNote && (
        <div className="flex items-start gap-2 p-3 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg mb-4">
          <Info className="w-3.5 h-3.5 text-[#5a5a5a] shrink-0 mt-0.5" />
          <p className="text-xs text-[#6b6b6b]">{service.rateNote}</p>
        </div>
      )}

      {/* Shared key note */}
      {service.sharedKeyNote && isConfigured && (
        <div className="flex items-start gap-2 p-3 bg-[#0d0d0d] border border-[#1e2a2a] rounded-lg mb-4">
          <CheckCircle className="w-3.5 h-3.5 text-[#06b6d4] shrink-0 mt-0.5" />
          <p className="text-xs text-[#64a0a8]">{service.sharedKeyNote}</p>
        </div>
      )}

      {/* Renew note */}
      {service.renewNote && (
        <div className="flex items-start gap-2 p-3 bg-[#0d0d0d] border border-[#2a1a0a] rounded-lg mb-4">
          <RefreshCw className="w-3.5 h-3.5 text-[#f59e0b] shrink-0 mt-0.5" />
          <p className="text-xs text-[#f59e0b]">{service.renewNote}</p>
        </div>
      )}

      {/* How to — collapsible */}
      {service.how.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setShowHow(h => !h)}
            className="flex items-center gap-1.5 text-xs text-[#94a3b8] hover:text-[#9a9a9a] transition-colors"
          >
            <Info className="w-3 h-3" />
            {showHow ? t('settings.hideInstructions') : t('settings.howToGet')}
          </button>
          {showHow && (
            <ol className="mt-2 space-y-1 pl-2">
              {service.how.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-[#94a3b8]">
                  <span className="shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-[#1a1a1a] text-[#64748b] font-bold text-[10px] mt-0.5">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Fields */}
      <div className="space-y-3">

        {/* CONFIGURED MODE */}
        {isConfigured && !editing && (
          <>
            {service.fields.map((field, fi) => {
              if (field.type === 'divider') {
                return (
                  <div key={'div-' + fi} className="flex items-center gap-2 pt-1 pb-0.5">
                    <div className="h-px flex-1 bg-[#2a2a2a]" />
                    <span className="text-xs text-[#444] uppercase tracking-widest whitespace-nowrap">{field.label}</span>
                    <div className="h-px flex-1 bg-[#2a2a2a]" />
                  </div>
                )
              }
              const val = savedValues[field.key] || ''
              const showAsText = !field.secret || visible[field.key]
              return (
                <div key={field.key}>
                  <label className="text-xs text-[#94a3b8] font-medium mb-1 block uppercase tracking-wide">
                    {field.label}
                  </label>
                  <div className="relative">
                    <input
                      type={showAsText ? 'text' : 'password'}
                      value={val}
                      readOnly
                      className="w-full px-3 py-2 pr-9 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg text-sm text-[#cccccc] focus:outline-none font-mono cursor-default"
                    />
                    {field.secret && val.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setVisible(v => ({ ...v, [field.key]: !v[field.key] }))}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#8b949e] transition-colors"
                      >
                        {visible[field.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
            {service.fields.filter(f => f.key).length > 0 && (
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#1a1a1a] hover:bg-[#222] text-[#8b949e] border border-[#2a2a2a] transition-all"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {t('settings.updateKeys')}
                </button>
                <button
                  onClick={handleClear}
                  disabled={clearing}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#1a0d0d] hover:bg-[#250d0d] text-[#ef4444] border border-[#3a1a1a] transition-all"
                >
                  {clearing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  {clearing ? t('settings.deleting') : t('settings.deleteKeys')}
                </button>
              </div>
            )}
          </>
        )}

        {/* PENDING or EDIT MODE */}
        {(!isConfigured || editing) && (
          <>
            {editing && (
              <p className="text-xs text-[#f59e0b] flex items-center gap-1.5 mb-1">
                <AlertTriangle className="w-3 h-3" />
                {t('settings.leaveEmpty')}
              </p>
            )}
            {!editing && service.fields.length > 0 && (
              <p className="text-xs text-[#64748b] mb-1">
                {t('settings.enterKeys')}
              </p>
            )}

            {service.fields.map((field, fi) => (
              field.type === 'divider' ? (
                <div key={'div-' + fi} className="flex items-center gap-2 pt-1 pb-0.5">
                  <div className="h-px flex-1 bg-[#2a2a2a]" />
                  <span className="text-xs text-[#444] uppercase tracking-widest whitespace-nowrap">{field.label}</span>
                  <div className="h-px flex-1 bg-[#2a2a2a]" />
                </div>
              ) : (
              <div key={field.key}>
                <label className="text-xs text-[#94a3b8] font-medium mb-1 block uppercase tracking-wide">
                  {field.label}
                </label>
                <div className="relative">
                  <input
                    type={field.secret && !visible[field.key] ? 'password' : 'text'}
                    value={values[field.key] || ''}
                    onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 pr-9 bg-[#0a0a0a] border border-[#333] rounded-lg text-sm text-[#cccccc] placeholder-[#3a3a3a] focus:outline-none focus:border-[#4a4a4a] transition-colors font-mono"
                    autoComplete="off"
                  />
                  {field.secret && (
                    <button
                      type="button"
                      onClick={() => setVisible(v => ({ ...v, [field.key]: !v[field.key] }))}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#8b949e] transition-colors"
                    >
                      {visible[field.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
              )
            ))}

            {service.fields.filter(f => f.key).length > 0 && (
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={handleSave}
                  disabled={!hasNewValues || saving}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    saveOk
                      ? 'bg-[#1a2a1a] text-[#22c55e] border border-[#2a3a2a]'
                      : hasNewValues
                        ? 'bg-[#0ea5e9] hover:bg-[#0284c7] text-white border border-transparent'
                        : 'bg-[#111] text-[#64748b] border border-[#1e1e1e] cursor-not-allowed'
                  }`}
                >
                  {saving ? <Loader className="w-4 h-4 animate-spin" />
                    : saveOk ? <Check className="w-4 h-4" />
                      : <Save className="w-4 h-4" />}
                  {saving ? t('settings.saving') : saveOk ? t('settings.saved') : t('settings.saveKeys')}
                </button>

                {editing && (
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 rounded-lg text-sm text-[#64748b] hover:text-[#8b949e] border border-[#2a2a2a] transition-colors"
                  >
                    {t('settings.cancel')}
                  </button>
                )}
              </div>
            )}

            {!serverAvailable && service.fields.length > 0 && (
              <p className="text-xs text-[#ef4444] flex items-center gap-1 mt-1">
                <AlertTriangle className="w-3 h-3" />
                {t('settings.noServerMsg')}
              </p>
            )}
          </>
        )}
      </div>

      <ApiHealthNote health={health} serviceId={service.id} />
    </div>
  )
}

export default function SettingsPage({ onBack }) {
  const { t } = useLanguage()
  const [status, setStatus]           = useState({})
  const [savedValues, setSavedValues] = useState({})
  const [serverAvailable, setServerAvailable] = useState(false)
  const [loading, setLoading]         = useState(true)
  const [saveMessage, setSaveMessage] = useState(null)
  const [healthApis, setHealthApis]   = useState({})

  const SERVICES = getServices(t)

  const loadData = async () => {
    try {
      const [statusRes, valuesRes] = await Promise.all([
        fetch(`${NODE_API}/api/settings/status`, { signal: AbortSignal.timeout(4000) }),
        fetch(`${NODE_API}/api/settings/values`,  { signal: AbortSignal.timeout(4000) }),
      ])
      if (!statusRes.ok || !valuesRes.ok) throw new Error('Server error')
      const [statusData, valuesData] = await Promise.all([statusRes.json(), valuesRes.json()])
      setStatus(statusData)
      setSavedValues(valuesData)
      setServerAvailable(true)
    } catch {
      setServerAvailable(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    fetch(`${NODE_API}/api/health/live`, { signal: AbortSignal.timeout(25000) })
      .then(r => r.json())
      .then(data => setHealthApis(data.apis || {}))
      .catch(() => {})
  }, [])

  const handleSave = async (values) => {
    const payload = {}
    Object.entries(values).forEach(([k, v]) => {
      if (v && v.length > 0) payload[k] = v
    })
    if (Object.keys(payload).length === 0) return
    const res = await fetch(`${NODE_API}/api/settings/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      // p. ej. Facebook rechazó el token: se muestra el motivo real, no un fallo silencioso
      const err = await res.json().catch(() => ({}))
      setSaveMessage(err.message || 'Save failed')
      setTimeout(() => setSaveMessage(null), 10000)
      throw new Error(err.message || 'Save failed')
    }
    const data = await res.json()
    await loadData()
    setSaveMessage(data.message || t('settings.saveKeys'))
    setTimeout(() => setSaveMessage(null), 4000)
  }

  const handleClear = async (fields) => {
    const res = await fetch(`${NODE_API}/api/settings/clear`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    })
    if (!res.ok) throw new Error('Clear failed')
    const data = await res.json()
    await loadData()
    setSaveMessage(data.message || t('settings.deleteKeys'))
    setTimeout(() => setSaveMessage(null), 4000)
  }

  const activeCount = [
    status.google_trends,
    status.amazon, status.pinterest,
    status.google_maps, status.facebook, status.facebook_rapidapi, status.aliexpress,
    status.google_ads, status.dataforseo,
    status.similarweb, status.semrush, status.ahrefs,
  ].filter(Boolean).length

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }}>
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-2 bg-[#111111] border border-[#2a2a2a] rounded-lg text-[#8b949e] hover:text-[#aaaaaa] text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('settings.back')}
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg">
              <Settings className="w-5 h-5 text-[#8b949e]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{t('settings.title')}</h1>
              <p className="text-xs text-[#8b949e]">
                {serverAvailable ? t('settings.connectedToServer') : t('settings.noConnection')}
              </p>
            </div>
          </div>
        </div>

        <AssistantKeyCard />

        {/* Progress */}
        <div className="p-5 bg-[#111111] border border-[#2a2a2a] rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-[#cccccc]">{t('settings.integrationStatus')}</p>
              <p className="text-xs text-[#8b949e] mt-0.5">{activeCount} {t('settings.of5Active')}</p>
            </div>
            <p className="text-3xl font-bold text-white">
              {activeCount}<span className="text-lg text-[#64748b]">/11</span>
            </p>
          </div>
          <div className="h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${(activeCount / 11) * 100}%`,
                background: activeCount === 11
                  ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                  : 'linear-gradient(90deg, #0ea5e9, #06b6d4)'
              }}
            />
          </div>
          <div className="flex gap-4 mt-3 flex-wrap">
            {[
              { label: 'Google Trends', ok: status.google_trends },
              { label: 'Amazon',        ok: status.amazon },
              { label: 'Pinterest',     ok: status.pinterest },
              { label: 'Google Maps',   ok: status.google_maps },
              { label: 'Facebook Ads',  ok: status.facebook },
              { label: 'Facebook Ads (RapidAPI)', ok: status.facebook_rapidapi },
              { label: 'AliExpress',    ok: status.aliexpress },
              { label: 'SimilarWeb',    ok: status.similarweb },
              { label: 'SEMrush',       ok: status.semrush },
              { label: 'Ahrefs',        ok: status.ahrefs },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${item.ok ? 'bg-[#22c55e]' : 'bg-[#ef4444]'}`} />
                <span className={`text-xs ${item.ok ? 'text-[#8b949e]' : 'text-[#64748b]'}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Save feedback */}
        {saveMessage && (
          <div className="flex items-center gap-2 p-3 bg-[#0d1f0d] border border-[#1a3a1a] rounded-lg text-sm text-[#22c55e]">
            <CheckCircle className="w-4 h-4 shrink-0" />
            {saveMessage}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-3 p-6 text-[#8b949e]">
            <Loader className="w-4 h-4 animate-spin" />
            <span className="text-sm">{t('settings.connectedToServer')}...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {SERVICES.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                savedValues={savedValues}
                status={status}
                health={healthApis[service.statusKey]}
                onSave={handleSave}
                onClear={handleClear}
                serverAvailable={serverAvailable}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  )
}
